import React, { useState, useContext } from "react";

import AppBar from "@material-ui/core/AppBar";
import axios from "axios";
import Button from "@material-ui/core/Button";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ReactGA from "react-ga";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import useForm from "react-hook-form";
import { Link, Redirect, Route } from "react-router-dom";
import { parse } from "query-string";

import config from "../../dashboard-config";

/* context for hooks */
import { SessionContext } from "../../context/SessionContext";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexWrap: "wrap"
  },
  formContainer: {
    marginTop: "3vh"
  },
  inputLabel: {
    fontWeight: 700,
    fontSize: "1.1em",
    paddingLeft: "0.5vw"
  },
  link: {
    textDecoration: "none"
  },
  linkContainer: {
    fontSize: "0.9em"
  },
  login: {
    backgroundColor: "#228CBD",
    borderRadius: "2px",
    margin: "3vh 0 2vh 0",
    padding: "1vh 2vw",
    textTransform: "none",
    width: "50%",

    "&:hover": {
      backgroundColor: "#262A3F"
    }
  },
  loginContainer: {
    backgroundColor: "#FFF",
    borderRadius: "2px",
    minWidth: "85vw",
    minHeight: "50vh",
    padding: "4vh 4vw 0 4vw",
    "@media only screen and (max-height: 725px)": {
      paddingBottom: "5vh",
      minHeight: "70vh"
    },
    "@media only screen and (max-height: 900px)": {
      paddingBottom: "5vh"
    },
    "@media only screen and (min-width: 600px)": {
      minWidth: "50vw"
    },
    "@media only screen and (min-width: 800px)": {
      minWidth: "40vw"
    },
    "@media only screen and (min-width: 1000px)": {
      minWidth: "30vw"
    }
  },
  termsBar: {
    background: "rgba(37, 42, 63, 0.5)",
    bottom: "0",
    fontSize: "0.8em",
    marginRight: theme.spacing(2),
    position: "absolute"
  },
  termLinks: {
    marginLeft: "auto",
    textDecoration: "none"
  },
  textField: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: "90%"
  }
}));

const Login = props => {
  const classes = useStyles();
  const [isError, setIsError] = useState(false);
  const { currentSession, setCurrentSession } = useContext(SessionContext);
  const { register, handleSubmit } = useForm({});

  const postLogin = async data => {
    /* URI encoding object */
    let details = {
      username: data.email,
      password: data.password,
      skipRedirect: true
    };

    let formBody = [];
    for (let property in details) {
      let encodedKey = encodeURIComponent(property);
      let encodedValue = encodeURIComponent(details[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    formBody = formBody.join("&");

    axios
      .post(`${config.wavefleetUrl}/login/`, formBody, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      })
      .then(res => {
        if (res.status === 200) {
          localStorage.setItem(
            "sofar-user",
            JSON.stringify({
              token: res.data.token,
              username: res.data.username
            })
          );
          setCurrentSession({
            ...currentSession,
            isLogged: true
          });
        } else {
          setIsError(true);
        }
      })
      .catch(e => {
        setIsError(true);
      });
  };

  const { username, token, redirect } = parse(props.location.search);
  if (token && username) {
    localStorage.setItem(
      "sofar-user",
      JSON.stringify({
        token,
        username
      })
    );
    setCurrentSession({
      ...currentSession,
      isLogged: true
    });
  }

  if (currentSession.isLogged) {
    ReactGA.set({ userId: 123 });
    ReactGA.event({
      category: "Login",
      action: "Successfully Logged In"
    });
    if (redirect) {
      return (
        <Route
          component={() => {
            window.location.replace(redirect);
            return null;
          }}
        />
      );
    }
    return <Redirect to="/" />;
  }

  return (
    <div className="auth-container">
      <Grid
        container
        alignItems="center"
        direction="column"
        justify="center"
        spacing={0}
        style={{ minHeight: "94vh" }}
      >
        <Grid item className={classes.loginContainer} xs={4}>
          <Typography
            variant="h4"
            style={{ fontWeight: "900", paddingLeft: "0.5vw" }}
          >
            Log in
          </Typography>
          <form className={classes.formContainer} noValidate autoComplete="off">
            <Typography variant="h6" className={classes.inputLabel}>
              Email Address or Username
            </Typography>
            <TextField
              id="standard-basic"
              className={classes.textField}
              fullWidth={true}
              name="email"
              margin="normal"
              variant="outlined"
              inputRef={register({
                required: "Field is empty!"
              })}
              data-test="emailInput"
            />
            <Typography variant="h6" className={classes.inputLabel}>
              Password
            </Typography>
            <TextField
              variant="outlined"
              id="standard-basic"
              className={classes.textField}
              fullWidth={true}
              margin="normal"
              name="password"
              type="password"
              inputRef={register({
                required: "Field is empty!"
              })}
              data-test="passwordInput"
            />
          </form>

          {isError && (
            <Typography
              className={classes.linkContainer}
              data-test="incorrectCredentialsError"
            >
              Credentials are incorrect!
            </Typography>
          )}

          <div style={{ textAlign: "left", paddingLeft: "0.5vw" }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.login}
              onClick={handleSubmit(postLogin)}
              data-test="loginButton"
            >
              Log in
            </Button>

            <hr
              style={{
                border: "1px solid #C7D9DF",
                width: "30%",
                marginBottom: "1vh"
              }}
            />

            <Typography
              className={classes.linkContainer}
              style={{ color: "#262A3F" }}
              variant="subtitle1"
            >
              Don't have an account? Sign up{" "}
              <Link to="/signup" className={classes.link}>
                here
              </Link>
            </Typography>
            <Typography className={classes.linkContainer} variant="subtitle1">
              <Link to="/recovery" className={classes.link}>
                Forgot your password?
              </Link>
            </Typography>
          </div>
        </Grid>
      </Grid>
      <AppBar className={classes.termsBar} position="static">
        <Toolbar>
          <span>© Sofar Ocean Technologies</span>
          <span className={classes.termLinks}>
            <a
              href="https://www.sofarocean.com/posts/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#FFF",
                padding: "0 1vw",
                textDecoration: "none"
              }}
            >
              Privacy Policy
            </a>
            <a
              href="https://www.sofarocean.com/posts/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#FFF",
                padding: "0 1vw",
                textDecoration: "none"
              }}
            >
              Terms of Use
            </a>
          </span>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default Login;
